import React, { useState } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Clock, RotateCcw } from "lucide-react";

/** Quick presets in addition to a date+time picker. */
const PRESETS = [
  { label: "1 day ago", days: 1 },
  { label: "7 days ago", days: 7 },
  { label: "30 days ago", days: 30 },
  { label: "90 days ago", days: 90 },
  { label: "1 year ago", days: 365 },
];

function localDatetimeValue(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function TimeTravelButton({ asOf, onChange }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(localDatetimeValue(asOf));

  const applyPreset = (days) => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    const iso = d.toISOString();
    setDraft(localDatetimeValue(iso));
    onChange(iso);
    setOpen(false);
  };

  const applyCustom = () => {
    if (!draft) return;
    const iso = new Date(draft).toISOString();
    onChange(iso);
    setOpen(false);
  };

  const clear = () => {
    setDraft("");
    onChange(null);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={`rounded-none border-neutral-300 ${asOf ? "border-[#5B5FC7] text-[#5B5FC7]" : ""}`}
          data-testid="time-travel-button"
        >
          <Clock size={14} className="mr-2"/>
          {asOf ? "Time travel · ON" : "Time travel"}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 rounded-none border-neutral-300 p-4">
        <p className="mb-2 text-[10px] uppercase tracking-[0.2em] text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
          View the org as it looked at
        </p>
        <div className="space-y-1">
          <Label className="text-[11px] uppercase tracking-wider text-neutral-500">Date &amp; time</Label>
          <Input
            type="datetime-local"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="rounded-none border-neutral-300"
            data-testid="time-travel-datetime-input"
          />
        </div>
        <div className="mt-3 flex flex-wrap gap-1">
          {PRESETS.map((p) => (
            <button
              key={p.label}
              onClick={() => applyPreset(p.days)}
              className="border border-neutral-300 px-2 py-1 text-[10px] uppercase tracking-wider hover:border-black"
              style={{ fontFamily: '"IBM Plex Mono", monospace' }}
              data-testid={`time-travel-preset-${p.days}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="mt-4 flex justify-between">
          {asOf ? (
            <button
              onClick={clear}
              className="flex items-center gap-1 text-[11px] uppercase tracking-wider text-[#C50F1F]"
              style={{ fontFamily: '"IBM Plex Mono", monospace' }}
              data-testid="time-travel-clear"
            >
              <RotateCcw size={11}/>
              Back to today
            </button>
          ) : <span/>}
          <Button onClick={applyCustom} disabled={!draft} className="h-7 rounded-none bg-[#002FA7] px-3 text-xs text-white hover:bg-[#001F70]" data-testid="time-travel-apply">
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
